'use client';

import { useI18n } from '@/contexts/I18nContext';

import dynamic from 'next/dynamic';
import { BarChart3, FileSearch, MapPin } from 'lucide-react';
import type { BookingProposal } from './CannyBookingCard';
import type { GenerationProposal } from './CannyGenerationCard';

/**
 * Карточки грузятся лениво.
 *
 * Большинство разговоров не доходит ни до брони, ни до генерации, ни до
 * загрузки референса — тащить их код в бандл страницы на всякий случай значит
 * платить весом за то, чего в чате чаще всего не происходит. Дробить тут
 * важнее, чем экономить импорты: страница открывается у каждого, карточка —
 * у немногих.
 */
const CannyBookingCard = dynamic(
  () => import('./CannyBookingCard').then((m) => m.CannyBookingCard),
  { ssr: false },
);
const CannyGenerationCard = dynamic(
  () => import('./CannyGenerationCard').then((m) => m.CannyGenerationCard),
  { ssr: false },
);
const CannyUploadWidget = dynamic(
  () => import('./CannyUploadWidget').then((m) => m.CannyUploadWidget),
  { ssr: false },
);
const CannyChoiceCard = dynamic(
  () => import('./CannyChoiceCard').then((m) => m.CannyChoiceCard),
  { ssr: false },
);
const CannyTrendCards = dynamic(
  () => import('./CannyTrendCards').then((m) => m.CannyTrendCards),
  { ssr: false },
);
const CannyCreatorCards = dynamic(
  () => import('./CannyCreatorCards').then((m) => m.CannyCreatorCards),
  { ssr: false },
);
const CannyStatsCard = dynamic(
  () => import('./CannyStatsCard').then((m) => m.CannyStatsCard),
  { ssr: false },
);

interface ToolPartLike {
  type: string;
  state?: string;
  input?: unknown;
  output?: unknown;
}

const QUIET_LABELS: Record<string, { key: string; Icon: typeof MapPin }> = {
  listVenues: { key: 'canny.tool.venues', Icon: MapPin },
  quoteCampaign: { key: 'canny.tool.quote', Icon: BarChart3 },
  searchBrandDocs: { key: 'canny.tool.docs', Icon: FileSearch },
  saveBrandFacts: { key: 'canny.tool.facts', Icon: FileSearch },
};

interface CannyToolPartProps {
  part: ToolPartLike;
  /** Куда сообщить о запуске генерации, чтобы он пережил перезагрузку. */
  onGenerationStarted?: (jobs: unknown[]) => void;
  /** Выбор варианта отправляет его как обычное сообщение. */
  onChoice?: (label: string) => void;
  /** Загруженный референс — общий на чат, используется генерацией. */
  referenceUrl?: string | null;
  onReferenceUploaded?: (url: string) => void;
}

/**
 * Разбирает части сообщения вида `tool-<имя>`.
 *
 * Платные предложения рисуются карточкой с кнопкой; справочные тулы — одной
 * тихой строкой, чтобы было видно, на чём Canny основывает ответ, но чтобы это
 * не забивало переписку.
 */
export function CannyToolPart({
  part,
  referenceUrl,
  onReferenceUploaded,
  onChoice,
  onGenerationStarted,
}: CannyToolPartProps) {
  const { t } = useI18n();
  if (typeof part.type !== 'string' || !part.type.startsWith('tool-')) return null;
  const name = part.type.slice('tool-'.length);

  if (name === 'proposeBooking') {
    const input = part.input as BookingProposal | undefined;
    // Вход приходит стримом по кускам: пока он не собран целиком, карточку
    // не рисуем, иначе она мигнёт пустой.
    if (!input?.stationIds || !input.from) return null;
    return <CannyBookingCard proposal={input} />;
  }

  if (name === 'proposeGeneration') {
    const input = part.input as GenerationProposal | undefined;
    if (!input?.shots || input.shots.length === 0) return null;
    // Запущенные ранее задания приходят в output — карточка открывается
    // сразу в работе, а не с активной кнопкой «Запустить».
    const saved = (part.output as { jobs?: unknown[] } | undefined)?.jobs;
    return (
      <CannyGenerationCard
        proposal={input}
        referenceUrl={referenceUrl ?? null}
        savedJobs={Array.isArray(saved) ? (saved as never) : null}
        onJobsStarted={(jobs) => onGenerationStarted?.(jobs)}
      />
    );
  }

  if (name === 'requestReferenceImage') {
    const input = part.input as { title?: string } | undefined;
    if (!input?.title) return null;
    return (
      <CannyUploadWidget
        title={input.title}
        uploadedUrl={referenceUrl}
        onUploaded={(url) => onReferenceUploaded?.(url)}
      />
    );
  }

  if (name === 'offerChoices') {
    const input = part.input as
      | { title?: string; options?: Array<{ label: string; hint?: string }> }
      | undefined;
    if (!input?.title || !input.options?.length) return null;
    return (
      <CannyChoiceCard
        title={input.title}
        options={input.options}
        onPick={(label) => onChoice?.(label)}
        onSkip={() => undefined}
      />
    );
  }

  if (name === 'searchTrends') {
    // Справочный тул с выходом: карточки строим из результата, а не из входа.
    const output = part.output as { trends?: unknown[] } | undefined;
    if (!output?.trends?.length) return null;
    return <CannyTrendCards trends={output.trends as never} />;
  }

  if (name === 'findCreator') {
    const output = part.output as { creators?: unknown[] } | undefined;
    if (!output?.creators?.length) return null;
    return <CannyCreatorCards creators={output.creators as never} />;
  }

  if (name === 'getCampaignPerformance') {
    const output = part.output as { impressions?: number; error?: string } | undefined;
    // При ошибке карточку не рисуем: «нет такой кампании» модель скажет
    // словами, а сетка нулей читалась бы как реальный результат.
    if (!output || output.error || typeof output.impressions !== 'number') return null;
    return <CannyStatsCard stats={output as never} />;
  }

  const quiet = QUIET_LABELS[name];
  if (!quiet) return null;
  const { key, Icon } = quiet;
  return (
    <div className="my-1 flex items-center gap-1.5 text-[11px] text-white/30">
      <Icon className="h-3 w-3" />
      {t(key)}
    </div>
  );
}
